import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader } from 'components/Shared/Loader'
import { EditDriverAccountForm } from 'components/Drivers/EditDriverForm/EditDriverAccountForm'
import { DriverForm, driverSchema } from 'components/Drivers/DriverForm'
import { editDriver, fetchDriver } from 'services'
import { SuccessDialog } from 'components/Shared/Dialogs/SuccessDialog'
import { triggerFormSubmit } from 'utils/utils'
import { TitleBar } from 'components/Shared/CrudComponents/TitleBar'
import styles from 'components/Shared/CrudComponents/SimpleForm.module.css'
import { useErrorHandling } from 'context/errorHandling'
import { HTTP_404_ERROR, HTTP_500_ERROR } from 'Constants'
import { useNavigatePrevent } from 'context/navigatePrevent'
import { FormikForm } from 'components/Shared/FormikForm'
import Module from 'constants/customFieldModules'
import { DRIVER_ACCOUNT_WRITE, useAuth } from 'context/auth'

export function EditDriverForm (props) {
  const [editedDialogOpen, setEditedDialogOpen] = useState(false)
  const [driver, setDriver] = useState(null)
  const { hasAnyPrivileges } = useAuth()

  const { handleError } = useErrorHandling()

  const editDriverFormRef = useRef()

  const { setNavigationUnlocked } = useNavigatePrevent()

  useEffect(() => {
      fetchDriver(props.match.params.id).then(response => {
          setDriver(response)
        },
      ).catch(error => handleError(error, {
        [HTTP_404_ERROR]: 'notifications.error.driverNotFound'
      }).then(() => {
        setNavigationUnlocked(history => history.push('/drivers'))
      }))
    }, [props.match.params.id, handleError, setNavigationUnlocked],
  )

  const { t } = useTranslation()

  const onDriverEdit = useCallback((values, onSuccess) =>
    editDriver({
      id: driver.id,
      ...values,
    }).then(() => onSuccess(driver.id)
      .then(() => setEditedDialogOpen(true))
      .catch(handleError)
    ).catch(error => handleError(error, {
      [HTTP_404_ERROR]: 'notifications.error.driverNotFound',
      [HTTP_500_ERROR]: 'notifications.error.editDriver',
    })), [driver, handleError])

  return <div>
    <TitleBar
      buttonLabel={t('form.driver.submit')}
      onButtonClick={triggerFormSubmit(editDriverFormRef)}
      title={t('form.driver.editDriverTitle')}/>
    <SuccessDialog open={editedDialogOpen}
                   onClose={() => {
                     setEditedDialogOpen(false)
                     setNavigationUnlocked(history => history.push('/drivers'))
                   }} content={t('notifications.success.updatedDriver')}/>

    {driver ? <div className={styles.containerRow}>
        <FormikForm
          innerRef={editDriverFormRef}
          initialValues={{
            firstName: driver.firstName,
            lastName: driver.lastName,
            address: driver.address,
            phoneNumber: driver.phoneNumber,
            personalPhoneNumber: driver.personalPhoneNumber,
            idCardNumber: driver.idCardNumber,
            idCardExpirationDate: driver.idCardExpirationDate,
            driverLicenseNumber: driver.driverLicenseNumber,
            driverLicenseCategory: driver.driverLicenseCategory,
            driverLicenseExpirationDate: driver.driverLicenseExpirationDate,
            adrCertificateNumber: driver.adrCertificateNumber,
            adrCertificateExpirationDate: driver.adrCertificateExpirationDate,
            carrierId: driver.carrierId,
            filesIds: driver.files.map(file => file.id)
          }}
          validationSchema={driverSchema}
          onSubmit={onDriverEdit}
          customFields={{
            module: Module.Drivers,
            entityId: driver?.id
          }}>
          <DriverForm initialFiles={driver.files}/>
        </FormikForm>
        {hasAnyPrivileges([DRIVER_ACCOUNT_WRITE]) && <EditDriverAccountForm id={driver.id}/>}
      </div>
      :
      <Loader/>
    }
  </div>
}
